import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLine, Reveal, Overline } from "@/components/motion";
import { CtaBanner } from "@/components/CtaBanner";
import { EXPERIENCES, IMAGES, CONTACT, HOPE_PHOTOS } from "@/data/site";

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.9], [1, 0.15]);

  return (
    <div data-testid="home-page">
      {/* KINETIC HERO */}
      <section
        ref={heroRef}
        data-testid="home-hero"
        className="relative flex h-screen min-h-[640px] items-center overflow-hidden"
      >
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="absolute inset-0">
          <motion.img
            src={IMAGES.hero.url}
            alt={IMAGES.hero.alt}
            initial={{ scale: 1.18 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.6, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-abyss via-abyss/40 to-abyss/20" />

        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            <Overline className="mb-8">
              Charter cu Velier · Marea Neagră · {CONTACT.season}
            </Overline>
          </motion.div>

          <h1 className="font-serif text-4xl font-light leading-[1.06] tracking-tight text-ivory sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.35}>Nu doar navigăm.</MaskedLine>
            <MaskedLine delay={0.5} className="italic text-gold">
              Creăm amintiri
            </MaskedLine>
            <MaskedLine delay={0.65}>dincolo de orizont.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-8 max-w-lg text-base leading-relaxed text-ivory/75"
          >
            La bordul velierului Jeanneau Sun Odyssey 42.2, fiecare ieșire pe
            mare devine poveste: nunți la apus, cursuri de skipper, excursii la
            Balcik și croaziere de neuitat.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.9 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href={CONTACT.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-phone-cta"
              className="flex items-center justify-center gap-3 bg-gold px-8 py-4 text-xs font-normal uppercase tracking-[0.25em] text-abyss transition-colors duration-300 hover:bg-gold-hover"
            >
              Rezervă Experiența
            </a>
            <a
              href="#experiente"
              data-testid="hero-explore-cta"
              className="flex items-center justify-center gap-3 border border-white/30 px-8 py-4 text-xs font-normal uppercase tracking-[0.25em] text-ivory transition-colors duration-300 hover:border-gold hover:text-gold"
            >
              Descoperă <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        >
          <div className="h-14 w-px overflow-hidden bg-white/20">
            <motion.div
              animate={{ y: [-56, 56] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              className="h-1/2 w-px bg-gold"
            />
          </div>
        </motion.div>
      </section>

      {/* EDITORIAL MARQUEE */}
      <div
        data-testid="home-marquee"
        className="border-y border-white/10 bg-abyss py-6"
      >
        <Marquee speed={30} gradient={false} pauseOnHover>
          {[
            "Jeanneau Sun Odyssey 42.2",
            "Velierul Hope",
            "Marina Life Harbour · Limanu",
            "Excursii Balcik",
            "Excursii Varna",
            "Nunți pe Velier",
            "Team Building Balcik",
            "Sezonul 2027",
          ].map((text) => (
            <span
              key={text}
              className="mx-10 text-xs uppercase tracking-[0.4em] text-ivory/35"
            >
              {text} <span className="ml-10 text-gold/50">•</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* ABOUT */}
      <section data-testid="home-about" className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <div className="overflow-hidden">
                <motion.img
                  src="/hope/hope-5.jpg"
                  alt="Velierul Hope acostat în Marina Life Harbour, cu numele vizibil pe pupă"
                  initial={{ scale: 1.1 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
                  className="aspect-[4/5] w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-4 border border-gold/40 bg-abyss px-6 py-4 lg:-right-10">
                <p className="font-serif text-3xl text-gold">42.2</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-mist">
                  Sun Odyssey
                </p>
              </div>
            </div>
          </Reveal>

          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal>
              <Overline className="mb-6">Velierul & Echipajul</Overline>
              <h2 className="font-serif text-3xl font-light leading-tight text-ivory sm:text-4xl lg:text-5xl">
                O barcă clasică, <span className="italic text-gold">o echipă de poveste.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-mist">
                Sun Odyssey 42.2 este unul dintre cele mai iubite veliere de
                croazieră construite vreodată de Jeanneau: stabil, elegant și
                surprinzător de spațios. Cu cabine confortabile, cockpit larg și
                o punte generoasă, este la fel de potrivit pentru o nuntă
                intimă ca și pentru o croazieră de cinci zile.
              </p>
              <p className="mt-6 text-base leading-relaxed text-mist">
                La cârmă se află un skipper cu peste 15 ani de navigație pe
                Marea Neagră, care cunoaște fiecare golfuleț dintre Limanu
                și Cap Kaliakra.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-12 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
                {[
                  ["12,98 m", "Lungime"],
                  ["10", "Oaspeți la bord"],
                  ["15+", "Ani pe mare"],
                ].map(([val, label]) => (
                  <div key={label}>
                    <p className="font-serif text-3xl text-gold sm:text-4xl">{val}</p>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-mist">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* EXPERIENCES BENTO */}
      <section
        id="experiente"
        data-testid="home-experiences"
        className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-12 lg:pb-32"
      >
        <Reveal className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <Overline className="mb-6">Experiențe</Overline>
            <h2 className="font-serif text-3xl font-light leading-tight text-ivory sm:text-4xl lg:text-5xl">
              Opt moduri de a <span className="italic text-gold">părăsi uscatul.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-mist">
            Fiecare experiență are propria pagină, cu program, detalii și
            tarife. Alege-ți povestea.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
          {EXPERIENCES.map((exp, i) => {
            const spans = [
              "md:col-span-7 aspect-[16/10]",
              "md:col-span-5 aspect-[16/10] md:aspect-auto",
              "md:col-span-5 aspect-[16/10] md:aspect-auto",
              "md:col-span-7 aspect-[16/10]",
              "md:col-span-6 aspect-[16/10]",
              "md:col-span-6 aspect-[16/10]",
              "md:col-span-12 aspect-[16/10] md:aspect-[21/7]",
              "md:col-span-12 aspect-[16/10] md:aspect-[21/7]",
            ];
            return (
              <Reveal key={exp.path} delay={i * 0.08} className={spans[i]}>
                <Link
                  to={exp.path}
                  data-testid={exp.testid}
                  className="group relative block h-full w-full overflow-hidden border border-white/10"
                >
                  <img
                    src={exp.image}
                    alt={exp.alt}
                    className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-abyss/95 via-abyss/25 to-transparent transition-opacity duration-500" />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6 lg:p-8">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                        0{i + 1}
                      </p>
                      <h3 className="mt-2 font-serif text-2xl font-light text-ivory sm:text-3xl">
                        {exp.title}
                      </h3>
                      <p className="mt-2 max-w-xs text-sm text-ivory/60 opacity-0 transition-all duration-500 group-hover:opacity-100">
                        {exp.tagline}
                      </p>
                    </div>
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/20 text-ivory transition-all duration-500 group-hover:border-gold group-hover:bg-gold group-hover:text-abyss">
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* REAL GALLERY */}
      <section data-testid="home-gallery" className="border-t border-white/10 bg-deep">
        <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
          <Reveal className="mb-16 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
            <div>
              <Overline className="mb-6">Galerie Reală</Overline>
              <h2 className="font-serif text-3xl font-light leading-tight text-ivory sm:text-4xl lg:text-5xl">
                Acesta este Hope. <span className="italic text-gold">Fără filtre.</span>
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-mist">
              Fotografii reale, făcute la bord și în Marina Life Harbour, Limanu
              — de la punte până în cabine, exact barca pe care vei naviga.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-8">
            {HOPE_PHOTOS.map((photo, i) => {
              const layouts = [
                "md:col-span-7 h-[420px] md:h-[560px]",
                "md:col-span-5 h-[420px] md:h-[560px]",
                "md:col-span-6 h-[340px] md:h-[440px]",
                "md:col-span-6 h-[340px] md:h-[440px]",
                "md:col-span-5 h-[380px] md:h-[500px]",
                "md:col-span-7 h-[380px] md:h-[500px]",
                "md:col-span-6 h-[340px] md:h-[440px]",
                "md:col-span-6 h-[340px] md:h-[440px]",
                "md:col-span-7 h-[380px] md:h-[480px]",
                "md:col-span-5 h-[380px] md:h-[480px]",
              ];
              return (
                <Reveal key={photo.src} delay={i * 0.08} className={layouts[i]}>
                  <figure
                    data-testid={`gallery-photo-${i + 1}`}
                    className="group relative h-full w-full overflow-hidden border border-white/10"
                  >
                    <img
                      src={photo.src}
                      alt={photo.alt}
                      className="h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-abyss/80 via-transparent to-transparent" />
                    <figcaption className="absolute bottom-0 left-0 p-5 text-[10px] uppercase tracking-[0.25em] text-ivory/80 lg:p-6 lg:text-xs">
                      {photo.caption}
                    </figcaption>
                  </figure>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="home-testimonials" className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-32">
        <Reveal className="mb-16">
          <Overline className="mb-6">Recenzii</Overline>
          <h2 className="font-serif text-3xl font-light text-ivory sm:text-4xl lg:text-5xl">
            Cuvintele celor care <span className="italic text-gold">au urcat la bord.</span>
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          {[
            {
              quote: "Am rezervat croaziera la apus pentru aniversarea noastră. Cea mai frumoasă seară din ultimii ani — lumina, șampania, liniștea mării. Echipa a avut grijă de absolut tot.",
              name: "Andreea & Mihai",
              tag: "Croazieră la Apus",
            },
            {
              quote: "Am făcut modulele de inițiere și perfecționare. Instruirea e serioasă, dar atmosfera relaxată — am stat la cârmă din prima zi. Recomand oricui vrea să învețe cu adevărat navigația.",
              name: "Vlad C.",
              tag: "Training Skipper",
            },
            {
              quote: "Team building-ul la Balcik a depășit orice așteptare. Oamenii care abia se salutau vineri dimineața lucrau ca un echipaj duminică seara. Colegii încă vorbesc despre weekendul acela.",
              name: "Diana P., HR Manager",
              tag: "Team Building Balcik",
            },
          ].map((t, i) => (
            <Reveal key={t.name} delay={i * 0.12}>
              <figure
                data-testid={`testimonial-${i + 1}`}
                className="flex h-full flex-col border border-white/10 p-8 transition-colors duration-500 hover:border-gold/40 lg:p-10"
              >
                <span aria-hidden="true" className="font-serif text-6xl leading-none text-gold/40">
                  &ldquo;
                </span>
                <blockquote className="mt-2 flex-1 font-serif text-lg font-light leading-relaxed text-ivory/90">
                  {t.quote}
                </blockquote>
                <figcaption className="mt-8 border-t border-white/10 pt-6">
                  <p className="text-sm text-ivory">{t.name}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-gold">{t.tag}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SEASON STRIP */}
      <section data-testid="home-season" className="border-t border-white/10">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-10 px-6 py-24 sm:grid-cols-3 lg:px-12 lg:py-32">
          {[
            ["Mai — Octombrie 2027", "Sezonul de navigație", "Rezervările sunt deschise pentru toate weekendurile sezonului viitor."],
            ["De la 3 ore la 5 zile", "Durata experiențelor", "De la o croazieră la apus până la o expediție pe coasta bulgărească."],
            ["Limanu → Kaliakra", "Ruta noastră", "Marina Life Harbour este casa; Marea Neagră, terenul de joacă."],
          ].map(([big, label, desc], i) => (
            <Reveal key={label} delay={i * 0.12}>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</p>
              <p className="mt-4 font-serif text-2xl font-light text-ivory">{big}</p>
              <p className="mt-4 text-sm leading-relaxed text-mist">{desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner />
    </div>
  );
};

export default Home;
